import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { cn } from "../lib/utils";
import { Calendar, Clock, Palette } from "lucide-react";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Event colors - matching backend
const EVENT_COLORS = [
  { id: "blue", name: "Blue", bg: "bg-blue-500", ring: "ring-blue-500" },
  { id: "green", name: "Green", bg: "bg-green-500", ring: "ring-green-500" },
  { id: "purple", name: "Purple", bg: "bg-purple-500", ring: "ring-purple-500" },
  { id: "red", name: "Red", bg: "bg-red-500", ring: "ring-red-500" },
  { id: "orange", name: "Orange", bg: "bg-orange-500", ring: "ring-orange-500" },
  { id: "yellow", name: "Yellow", bg: "bg-yellow-500", ring: "ring-yellow-500" },
  { id: "teal", name: "Teal", bg: "bg-teal-500", ring: "ring-teal-500" },
  { id: "pink", name: "Pink", bg: "bg-pink-500", ring: "ring-pink-500" },
];

const EventDialog = ({ open, onOpenChange, event, onSave }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("blue");
  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Reset form when dialog opens/closes or event changes
  useEffect(() => {
    if (open) {
      if (event) {
        setName(event.name || "");
        setColor(event.color || "blue");
        setDate(event.date ? new Date(event.date + "T00:00:00") : null);
        setStartTime(event.start_time || "09:00");
        setEndTime(event.end_time || "17:00");
        setNotes(event.notes || "");
      } else {
        // Default values for new event
        setName("");
        setColor("blue");
        setDate(new Date());
        setStartTime("09:00");
        setEndTime("17:00");
        setNotes("");
      }
    }
  }, [open, event]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }
    
    if (!date) {
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        color,
        date: format(date, "yyyy-MM-dd"),
        start_time: startTime,
        end_time: endTime,
        notes: notes.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!event;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Event" : "Add Event"}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Update the event details below."
                : "Create a new event to display on your rota timeline."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Event Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">Event Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Office Party, Team Meeting"
                data-testid="event-name-input"
                required
              />
            </div>

            {/* Color Selection */}
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Color
              </Label>
              <div className="flex flex-wrap gap-2">
                {EVENT_COLORS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      c.bg,
                      color === c.id && "ring-2 ring-offset-2",
                      color === c.id && c.ring
                    )}
                    onClick={() => setColor(c.id)}
                    title={c.name}
                    data-testid={`event-color-${c.id}`}
                  />
                ))}
              </div>
            </div>

            {/* Date */}
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date *
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                    data-testid="event-date-picker"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(selectedDate) => {
                      setDate(selectedDate);
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="startTime" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Start Time
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="event-start-time"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="endTime">End Time</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  data-testid="event-end-time"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional details about this event..."
                rows={3}
                data-testid="event-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim() || !date} data-testid="event-save-btn">
              {saving ? "Saving..." : isEditing ? "Update Event" : "Create Event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventDialog;
